/** "Pergunte à <empresa>": assistente sobre os dados do escritório. */
import { useEffect, useRef, useState } from 'react';
import { Icon, SheetHead } from '@dotapps/ui';
import { useTenant } from '../../app/tenant';
import { useSession } from '../../lib/hooks';
import { calls } from '../../lib/queries';

type Msg = { role: 'user' | 'assistant'; content: string; live?: boolean };

const QUESTIONS = [
  'Qual obra corre mais risco de atrasar?',
  'Quanto temos a receber nas obras em andamento?',
  'Quais clientes estão sem contato há mais de 7 dias?',
];

// conversa mantida enquanto a aba estiver aberta
let history: Msg[] = [];

export function AssistantDrawer({ initial }: { initial?: string }) {
  const { tenant } = useTenant();
  const { me } = useSession();
  const [msgs, setMsgs] = useState<Msg[]>(history);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const log = useRef<HTMLDivElement>(null);
  const asked = useRef(false);

  useEffect(() => {
    history = msgs;
    log.current?.scrollTo({ top: log.current.scrollHeight });
  }, [msgs]);

  async function ask(q: string) {
    if (!q.trim() || busy) return;
    const next: Msg[] = [...msgs, { role: 'user', content: q }];
    setMsgs(next);
    setBusy(true);
    try {
      const r = await calls.aiChat(next.slice(-6).map(({ role, content }) => ({ role, content })));
      setMsgs(m => [...m, { role: 'assistant', content: r.data, live: r.live }]);
    } catch (err) {
      setMsgs(m => [...m, { role: 'assistant', content: (err as Error).message, live: true }]);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (initial && !asked.current) {
      asked.current = true;
      void ask(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  return (
    <>
      <SheetHead
        icon={<Icon name="spark" />}
        title={` Pergunte à ${tenant.assistantName}`}
        sub="Respostas a partir dos dados do escritório."
      />
      <div className="sheet-body chat">
        <div className="chat-log" ref={log}>
          {msgs.length === 0 && (
            <div className="msg assistant">
              Olá, {me.name.split(' ')[0]}. Posso resumir negociações, apontar riscos de obra, somar valores a receber
              ou sugerir o próximo passo com um cliente.
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={`msg ${m.role}`}>
              {m.content}
              {m.role === 'assistant' && m.live === false && (
                <small className="ai-src">resposta automática · IA paga desligada</small>
              )}
            </div>
          ))}
          {busy && <div className="msg assistant typing">Pensando…</div>}
        </div>
        <div className="chips">
          {QUESTIONS.map(q => (
            <button key={q} className="chip" onClick={() => ask(q)}>
              {q}
            </button>
          ))}
        </div>
        <form
          className="chat-in"
          onSubmit={e => {
            e.preventDefault();
            void ask(text);
            setText('');
          }}
        >
          <input
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Pergunte algo…"
            autoComplete="off"
            aria-label="Pergunta"
          />
          <button className="btn primary" type="submit" aria-label="Enviar" disabled={busy}>
            <Icon name="arrow" />
          </button>
        </form>
      </div>
    </>
  );
}
